import { useLang } from "@/context/LanguageContext";
import { Compass, MoveRight } from "lucide-react";
import { NALJURI_PHOTOS } from "@/lib/photos";
import { NEARBY } from "@/lib/menu";
import { WA_NEARBY_ATTRACTIONS } from "@/lib/contact";

export default function NearbyAttractions() {
  const { lang } = useLang();

  const head = lang === 'en'
    ? { eyebrow: "10 marquee destinations · 1 basecamp", title: "At the centre of every Sylhet you came for.", sub: "Every iconic Sylhet destination — Sadapathor, Lalakhal, Jaflong, Pangthumai, Ratargul — sits within a 90-minute drive of your bed. Naljuri Inn is the strategic basecamp." }
    : { eyebrow: "১০টি গন্তব্য · ১টি বেসক্যাম্প", title: "সিলেটের সব দর্শনীয় স্থানের ঠিক মাঝখানে।", sub: "সাদাপাথর, লালাখাল, জাফলং, পাংথুমাই, রাতারগুল — সিলেটের প্রত্যেকটি আইকনিক গন্তব্য আপনার বিছানা থেকে ৯০ মিনিটের মধ্যে। নলজুরি ইন আপনার কৌশলগত বেসক্যাম্প।" };

  return (
    <section id="nearby" className="bg-white" data-testid="nearby-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="eyebrow tag-line">{head.eyebrow}</p>
            <h2 className={`mt-4 text-4xl md:text-5xl font-display text-forest-700 tracking-tight leading-[1.05] ${lang==='bn'?'font-bn':''}`}
                data-testid="nearby-title">
              {head.title}
            </h2>
            <div className="gold-rule mt-6" />
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-base text-slate-warm leading-relaxed">{head.sub}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* Hub photograph */}
          <div className="lg:col-span-7 relative">
            <div className="rounded-md overflow-hidden border border-forest-100 bg-cream-100 group">
              <img src={NALJURI_PHOTOS.NEARBY_HUB}
                   alt={lang === 'en' ? "Naljuri Inn at the centre of 10 destinations" : "১০টি গন্তব্যের কেন্দ্রে নলজুরি ইন"}
                   className="w-full h-[460px] md:h-[600px] object-cover img-zoom" />
            </div>
            <div className="absolute -bottom-4 -right-4 hidden md:flex items-center gap-2 bg-gold-600 text-cream-100 px-5 py-3 rounded-full shadow-[0_18px_40px_-18px_rgba(184,134,11,0.7)]">
              <Compass className="w-4 h-4" />
              <span className="text-[11px] uppercase tracking-widest2">Strategic basecamp</span>
            </div>
          </div>

          {/* Destination list */}
          <div className="lg:col-span-5">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="nearby-list">
              {NEARBY.map((d, i) => (
                <li key={i} className="group flex items-start gap-3 p-4 border border-forest-100 rounded-md hover:border-gold-600 hover:bg-cream-100 transition-colors"
                    data-testid={`nearby-item-${i}`}>
                  <span className="text-[10px] font-mono text-gold-600 mt-1">{String(i+1).padStart(2,'0')}</span>
                  <div>
                    <div className={`text-sm font-display text-forest-700 leading-tight ${lang==='bn'?'font-bn':''}`}>
                      {lang === 'en' ? d.en : d.bn}
                    </div>
                    <div className="text-[11px] text-slate-warm mt-0.5">{lang === 'en' ? d.note_en : d.note_bn}</div>
                  </div>
                </li>
              ))}
            </ul>

            <a href={WA_NEARBY_ATTRACTIONS} target="_blank" rel="noreferrer"
               data-testid="nearby-wa-link"
               className="mt-7 inline-flex items-center gap-2 bg-forest-700 text-cream-100 px-6 py-3 rounded-full text-sm hover:bg-forest-800 transition-colors border border-gold-600/40">
              {lang === 'en' ? 'Plan tours via WhatsApp' : 'হোয়াটসঅ্যাপে ট্যুর প্ল্যান করুন'} <MoveRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
